import React, { useEffect, useRef, useState } from 'react';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import './AddressTimeline.css';

const AddressTimeline = ({ timelineData }) => {
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const [containerReady, setContainerReady] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  // Ensure container is ready before rendering
  useEffect(() => {
    if (containerRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setContainerReady(true);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (!timelineData || !containerRef.current || !containerReady) return;
    if (!timelineData.timeline_events || timelineData.timeline_events.length === 0) return;

    const events = timelineData.timeline_events;
    const nodes = [];
    const edges = [];

    // Calculate timeline dimensions
    const minTime = events[0].timestamp;
    const maxTime = events[events.length - 1].timestamp;
    const timeRange = maxTime - minTime || 1;
    const timelineWidth = events.length * 75; // 75px per transaction
    const centerY = 0; // Center line
    const verticalSpacing = 150; // Spacing for input/output nodes

    // Add central address node
    nodes.push({
      id: 'central_address',
      label: `📍 ${timelineData.address.substring(0, 12)}...`,
      x: 0,
      y: centerY,
      fixed: true,
      shape: 'box',
      size: 20,
      color: {
        background: '#f7931a',
        border: '#ff8c00',
        highlight: {
          background: '#ff9800',
          border: '#ff7b00'
        }
      },
      font: { color: '#fff', size: 13, bold: true },
      borderWidth: 3,
      title: `
        <b>Address:</b> ${timelineData.address}<br/>
        <b>Balance:</b> ${timelineData.balance} BTC<br/>
        <b>Total Received:</b> ${timelineData.total_received} BTC<br/>
        <b>Total Sent:</b> ${timelineData.total_sent} BTC<br/>
        <b>Transactions:</b> ${timelineData.tx_count}
      `
    });

    // Create nodes for each transaction event
    events.forEach((event, index) => {
      // Equal spacing: distribute evenly regardless of time gaps
      const x = -timelineWidth / 2 + (index / (events.length - 1 || 1)) * timelineWidth;
      
      // Transaction node on timeline
      const txId = `tx_${event.txid}`;
      nodes.push({
        id: txId,
        label: `${event.net_change > 0 ? '+' : ''}${event.net_change.toFixed(4)} BTC`,
        x: x,
        y: centerY,
        fixed: true,
        shape: 'dot',
        size: 12,
        color: {
          background: event.net_change > 0 ? '#4caf50' : '#f44336',
          border: '#fff',
          highlight: {
            background: event.net_change > 0 ? '#66bb6a' : '#ef5350',
            border: '#fff'
          }
        },
        font: { color: '#ddd', size: 11 },
        title: `
          <b>Transaction:</b> ${event.txid.substring(0, 16)}...<br/>
          <b>Date:</b> ${new Date(event.timestamp * 1000).toLocaleString()}<br/>
          <b>Net Change:</b> ${event.net_change > 0 ? '+' : ''}${event.net_change.toFixed(8)} BTC<br/>
          <b>Balance After:</b> ${event.balance_after.toFixed(8)} BTC
        `
      });

      // Add time marker below timeline (every transaction or every 5th for many transactions)
      const showTimeMarker = events.length <= 20 || index % Math.ceil(events.length / 20) === 0;
      if (showTimeMarker) {
        const date = new Date(event.timestamp * 1000);
        const timeLabel = date.toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit', 
          year: '2-digit' 
        });
        
        const timeNodeId = `time_${event.txid}`;
        nodes.push({
          id: timeNodeId,
          label: timeLabel,
          x: x,
          y: centerY + 280,
          fixed: true,
          shape: 'box',
          size: 8,
          color: {
            background: '#2a2a3e',
            border: '#444',
            highlight: {
              background: '#3a3a4e',
              border: '#555'
            }
          },
          font: { color: '#888', size: 9 },
          title: `<b>Full Date:</b> ${date.toLocaleString('de-DE')}`
        });

        // Connect time markers with a timeline
        if (index > 0) {
          // Find previous time marker
          for (let i = index - 1; i >= 0; i--) {
            const prevShowMarker = events.length <= 20 || i % Math.ceil(events.length / 20) === 0;
            if (prevShowMarker) {
              const prevTimeNodeId = `time_${events[i].txid}`;
              edges.push({
                from: prevTimeNodeId,
                to: timeNodeId,
                color: { color: '#444', opacity: 0.5 },
                width: 2,
                smooth: false,
                arrows: { to: { enabled: false } },
                dashes: false
              });
              break;
            }
          }
        }
      }

      // Connect consecutive transactions with timeline edges
      if (index > 0) {
        const prevTxId = `tx_${events[index - 1].txid}`;
        edges.push({
          from: prevTxId,
          to: txId,
          color: { color: '#f7931a', opacity: 0.8 },
          width: 3,
          smooth: false,
          arrows: { to: { enabled: false } },
          dashes: false
        });
      }

      // Input nodes (above timeline) - these are external senders
      event.inputs.forEach((input, i) => {
        const nodeId = `in_${event.txid}_${i}`;
        const nodeY = centerY - verticalSpacing - (i * 80);
        
        nodes.push({
          id: nodeId,
          label: `${input.address.substring(0, 8)}...\n${input.value.toFixed(4)} BTC`,
          x: x,
          y: nodeY,
          fixed: true,
          shape: 'box',
          color: {
            background: '#4caf50',
            border: '#388e3c',
            highlight: {
              background: '#66bb6a',
              border: '#4caf50'
            }
          },
          font: { color: '#fff', size: 10, multi: true },
          title: `<b>From:</b> ${input.address}<br/><b>Value:</b> ${input.value.toFixed(8)} BTC`,
          // Store full data for click handler
          fullAddress: input.address,
          value: input.value,
          type: 'input',
          txid: event.txid
        });

        edges.push({
          from: nodeId,
          to: txId,
          arrows: 'to',
          color: { color: '#4caf50', opacity: 0.6 },
          smooth: { type: 'cubicBezier', roundness: 0.6 },
          width: 2
        });
      });

      // Output nodes (below timeline) - these are external receivers
      event.outputs.forEach((output, i) => {
        const nodeId = `out_${event.txid}_${i}`;
        const nodeY = centerY + verticalSpacing + (i * 80);
        
        nodes.push({
          id: nodeId,
          label: `${output.address.substring(0, 8)}...\n${output.value.toFixed(4)} BTC`,
          x: x,
          y: nodeY,
          fixed: true,
          shape: 'box',
          color: {
            background: '#f44336',
            border: '#d32f2f',
            highlight: {
              background: '#ef5350',
              border: '#f44336'
            }
          },
          font: { color: '#fff', size: 10, multi: true },
          title: `<b>To:</b> ${output.address}<br/><b>Value:</b> ${output.value.toFixed(8)} BTC`,
          // Store full data for click handler
          fullAddress: output.address,
          value: output.value,
          type: 'output',
          txid: event.txid
        });

        edges.push({
          from: txId,
          to: nodeId,
          arrows: 'to',
          color: { color: '#f44336', opacity: 0.6 },
          smooth: { type: 'cubicBezier', roundness: 0.6 },
          width: 2
        });
      });
    });

    // Create network
    const data = {
      nodes: new DataSet(nodes),
      edges: new DataSet(edges)
    };

    const options = {
      layout: {
        hierarchical: false
      },
      physics: {
        enabled: false
      },
      interaction: {
        hover: true,
        tooltipDelay: 100,
        zoomView: true,
        dragView: true,
        dragNodes: false
      },
      nodes: {
        borderWidth: 2,
        borderWidthSelected: 3
      },
      edges: {
        width: 2,
        selectionWidth: 3
      },
      autoResize: true,
      height: '600px',
      width: '100%'
    };

    try {
      requestAnimationFrame(() => {
        if (!containerRef.current) return;
        
        networkRef.current = new Network(containerRef.current, data, options);
        
        // Add click event handler
        networkRef.current.on('click', (params) => {
          if (params.nodes.length > 0) {
            const nodeId = params.nodes[0];
            const node = data.nodes.get(nodeId);
            
            // Only show popup for address nodes (not TX or time nodes)
            if (node && node.fullAddress) {
              setSelectedNode({
                address: node.fullAddress,
                value: node.value,
                type: node.type,
                txid: node.txid
              });
            }
          }
        });
        
        // Fit to view after short delay
        setTimeout(() => {
          if (networkRef.current) {
            networkRef.current.fit({
              animation: {
                duration: 500,
                easingFunction: 'easeInOutQuad'
              }
            });
          }
        }, 100);
      });
    } catch (error) {
      if (!error.message?.includes('ResizeObserver')) {
        console.error('Network graph error:', error);
      }
    }

    // Cleanup
    return () => {
      if (networkRef.current) {
        try {
          networkRef.current.destroy();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [timelineData, containerReady]);

  if (!timelineData) {
    return (
      <div className="timeline-placeholder">
        <p>🔍 Suche nach einer Adresse, um die Timeline zu sehen</p>
      </div>
    );
  }

  if (!timelineData.timeline_events || timelineData.timeline_events.length === 0) {
    return (
      <div className="timeline-placeholder">
        <p>⚠️ Keine Transaktionen gefunden</p>
        <small>Diese Adresse hat keine Transaktionshistorie</small>
      </div>
    );
  }

  return (
    <div className="address-timeline-container">
      <div className="timeline-header">
        <div className="timeline-header-content">
          <h3>{timelineData.address}</h3>
          <div className="timeline-stats">
            <div className="stat">
              <span className="stat-label">Balance:</span>
              <span className="stat-value">{timelineData.balance} BTC</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Received:</span>
              <span className="stat-value positive">{timelineData.total_received} BTC</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Sent:</span>
              <span className="stat-value negative">{timelineData.total_sent} BTC</span>
            </div>
            <div className="stat">
              <span className="stat-label">Transactions:</span>
              <span className="stat-value">{timelineData.tx_count}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="timeline-hint">
        <small>💡 Mausrad zum Zoomen, Ziehen zum Verschieben, Hover für Details</small>
      </div>
      <div 
        ref={containerRef} 
        className="timeline-graph-container"
        style={{ height: '600px', width: '100%' }}
      />
      
      {/* Address Details Modal */}
      {selectedNode && (
        <div className="address-modal-overlay" onClick={() => setSelectedNode(null)}>
          <div className="address-modal" onClick={(e) => e.stopPropagation()}>
            <div className="address-modal-header">
              <h3>{selectedNode.type === 'input' ? '📥 Eingehende Adresse' : '📤 Ausgehende Adresse'}</h3>
              <button className="modal-close" onClick={() => setSelectedNode(null)}>✕</button>
            </div>
            <div className="address-modal-body">
              <div className="modal-field">
                <label>Adresse:</label>
                <div className="modal-value address-value">
                  {selectedNode.address}
                  <button 
                    className="copy-btn" 
                    onClick={() => navigator.clipboard.writeText(selectedNode.address)}
                    title="Kopieren"
                  >
                    📋
                  </button>
                </div>
              </div>
              <div className="modal-field">
                <label>Betrag:</label>
                <div className="modal-value">{selectedNode.value.toFixed(8)} BTC</div>
              </div>
              <div className="modal-field">
                <label>Transaktion:</label>
                <div className="modal-value">
                  <a 
                    href={`https://mempool.space/tx/${selectedNode.txid}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="tx-link"
                  >
                    {selectedNode.txid}
                  </a>
                </div>
              </div>
              <div className="modal-actions">
                <a 
                  href={`https://mempool.space/address/${selectedNode.address}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="modal-btn"
                >
                  📊 Auf Mempool.space öffnen
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="timeline-table-container">
        <h4>Transaktionsliste</h4>
        <table className="timeline-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Transaction ID</th>
              <th>Typ</th>
              <th>Betrag</th>
              <th>Balance</th>
              <th>Partner</th>
            </tr>
          </thead>
          <tbody>
            {timelineData.timeline_events.map((event, index) => {
              const date = new Date(event.timestamp * 1000);
              const isReceived = event.net_change > 0;
              const partnerCount = isReceived ? event.inputs.length : event.outputs.length;
              
              return (
                <tr key={event.txid}>
                  <td>{date.toLocaleString('de-DE')}</td>
                  <td>
                    <a 
                      href={`https://mempool.space/tx/${event.txid}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="tx-link"
                    >
                      {event.txid.substring(0, 16)}...
                    </a>
                  </td>
                  <td>
                    <span className={`tx-type ${isReceived ? 'received' : 'sent'}`}>
                      {isReceived ? '📥 Empfang' : '📤 Sendung'}
                    </span>
                  </td>
                  <td className={isReceived ? 'positive' : 'negative'}>
                    {isReceived ? '+' : ''}{event.net_change.toFixed(8)} BTC
                  </td>
                  <td>{event.balance_after.toFixed(8)} BTC</td>
                  <td>
                    {partnerCount > 0 ? `${partnerCount} Adresse${partnerCount > 1 ? 'n' : ''}` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AddressTimeline;
